import { Component, OnDestroy, OnInit } from '@angular/core';
import { CoreService } from 'src/app/services/core.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/@auth/auth.service';
import { lastValueFrom, Subject } from 'rxjs';
import { AppService } from 'src/app/services/app.service';
import { AppInfos, CLU_MOBILE_INTERFACE } from 'src/app/app.models';
import { ApiService } from 'src/app/services/api.service';
import { environment } from 'src/environments/environment';
import { AuthUtils } from 'src/app/utils/auth.utils';
import { MfaLoginService } from '../mfa-login/mfa-login.service';
import { RenewExpiredPasswordService } from '../renew-expired-password/renew-expired-password.service';
import { TauriService } from 'src/app/services/tauri/tauri.service';
import { CluConfirmDialogService } from 'src/app/utils/clu-confirm-dialog/clu-confirm-dialog.service';

const LS_MOBILE_AUTH_SITE_ID_KEY = 'LS_MOBILE_AUTH_SITE_ID_KEY';
const LS_MOBILE_AUTH_USERNAME_KEY = 'LS_MOBILE_AUTH_USERNAME_KEY';
const LS_MOBILE_AUTH_INTERFACE_KEY = 'LS_MOBILE_AUTH_INTERFACE_KEY';

@Component({
  selector: 'mobile-login',
  templateUrl: './mobile-login.component.html',
})
export class MobileLoginComponent implements OnInit, OnDestroy {
    options = this.settings.getOptions();
    loading = false;
    errorText: string | null = null;
    appVersion: AppInfos;
    form = new FormGroup({
        Domaine: new FormControl('', [Validators.required]),
        CodeSite: new FormControl(''),
        Username: new FormControl('', [Validators.required]),
        Password: new FormControl('', [Validators.required]),
        Interface: new FormControl(CLU_MOBILE_INTERFACE.FRONTOFFICE, [Validators.required]),
    });
    CLU_MOBILE_INTERFACE = CLU_MOBILE_INTERFACE;
    suffixDomaine = '.caurislinkup.com';
    onComplete = new Subject<void>();
    isTauri = false;

    constructor(
        private settings: CoreService,
        private router: Router,
        private authService: AuthService,
        private apiService: ApiService,
        private mfaLoginService: MfaLoginService,
        private renewExpiredPasswordService: RenewExpiredPasswordService,
        private appService: AppService,
        private _tauriService: TauriService,
        private _confirmationService: CluConfirmDialogService,
    ) {
        if (this.appService.appVersion$.value.APP_ENVIRONMENT == 'preprod') { this.suffixDomaine = '.preprod.caurislinkup.fr'; }
        lastValueFrom(this.authService.doRemoveAuthSession());

        this.f.Domaine.patchValue(localStorage.getItem(environment.LS_MOBILE_AUTH_DOMAINE_KEY) || '');
        this.f.CodeSite.patchValue(localStorage.getItem(LS_MOBILE_AUTH_SITE_ID_KEY) || '');
        this.f.Username.patchValue(localStorage.getItem(LS_MOBILE_AUTH_USERNAME_KEY) || '');
        this.f.Interface.patchValue(localStorage.getItem(LS_MOBILE_AUTH_INTERFACE_KEY) == (CLU_MOBILE_INTERFACE.BACKOFFICE + '') ? CLU_MOBILE_INTERFACE.BACKOFFICE : CLU_MOBILE_INTERFACE.FRONTOFFICE);
    this.isTauri = this._tauriService.isTauri;
    }

    ngOnInit(): void {
      this.appService.appVersion$.subscribe(appVersion => {
        this.appVersion = appVersion;
      });
    }

    ngOnDestroy(): void {
      this.onComplete.next();
      this.onComplete.complete();
    }

    async doMinimizeApp() {
      await this._tauriService.minimizeApp();
    }

    async doQuitApp() {
      const confirmation = await this._confirmationService.showConfirmDialog("Cauris Linkup", "Voulez-vous vraiment quitter l'application ?");
      if (confirmation == true) {
        await this._tauriService.quitApp();
      }
    }

    get f() {
        return this.form.controls;
    }

  async submit() {
    if (this.form.invalid) { return; }

    this.errorText = null;
    this.loading = true;
    const domaine = this.f.Domaine.value!;
    const codeSite = this.f.CodeSite.value;
    const username = this.f.Username.value!;
    const password = this.f.Password.value!;
    const interfaceId = this.f.Interface.value!;
    try {
      localStorage.setItem(environment.LS_MOBILE_AUTH_DOMAINE_KEY, domaine);
      this.apiService.reloadEndpoints();
      let token = await this.authService.doAuthenticateMobileAction(domaine, codeSite, username, password, interfaceId);
      const claims = AuthUtils.parseJwt(token);
      if (claims.MFA_ENABLED == '1') {
        token = await this.mfaLoginService.show({ AccessToken: token, CluInterface: interfaceId });
        if (!token) {
          throw "";
        }
      }

      const authSessionDto = await this.authService.doGetAuthMobileSession(token, interfaceId);
      if (authSessionDto?.HAS_DEFAULT_PASSWORD === true) {
        const isPasswordRenewed = await this.renewExpiredPasswordService.show({ AccessToken: token });
        if (!isPasswordRenewed) {
          throw "Mot de passe non initialisé";
        }
      }

      localStorage.setItem(LS_MOBILE_AUTH_INTERFACE_KEY, interfaceId.toString());
      if (!!codeSite) { localStorage.setItem(LS_MOBILE_AUTH_SITE_ID_KEY, codeSite); }
      localStorage.setItem(LS_MOBILE_AUTH_USERNAME_KEY, username);
      this.appVersion.TYPE_LICENCE = authSessionDto.TYPE_LICENCE;
      this.appService.appVersion$.next(this.appVersion);
      this.authService.saveAuthenticationSession(authSessionDto);
      let nextUrl = interfaceId == CLU_MOBILE_INTERFACE.FRONTOFFICE ? '/apps/cashdesk/landing' : '/cluadmin/apps/p-admin/adm-sites';
      await this.router.navigateByUrl(nextUrl);
      this.loading = false;
    } catch(e: any) {
      this.errorText = e;
      this.loading = false;
    }

  }
}
